import { debounce } from 'lodash';
import { FC, useRef, useState } from 'react';

import {
	Box,
	Center,
	Input,
	Popover,
	PopoverAnchor,
	PopoverBody,
	PopoverContent,
	rem,
	Text,
	useDisclosure,
	useOutsideClick,
	VStack,
} from '@bonusly/design-system';

import { CustomPropertyUserGroup } from '../../queries/groups';
import { Reveal } from '../display/reveal';

type AttributeSearchProps = {
	isDisabled?: boolean;
	onSelect: (value: string) => void;
	options: CustomPropertyUserGroup[];
	placeholder?: string;
	noResultsMessage: string;
};

export const AttributeSearch: FC<AttributeSearchProps> = ({ isDisabled, options, onSelect, placeholder, noResultsMessage }) => {
	const popover = useDisclosure();
	const [displayedOptions, setDisplayedOptions] = useState<CustomPropertyUserGroup[]>([]);

	const wrapperRef = useRef<HTMLDivElement>(null);

	const handleChange = (value: string) => {
		if (value) {
			popover.onOpen();
			setDisplayedOptions(options.filter((option) => option.name.toLowerCase().includes(value.toLowerCase())).slice(0, 20));
		} else {
			popover.onClose();
		}
	};

	useOutsideClick({
		ref: wrapperRef,
		handler: () => {
			popover.onClose();
		},
	});

	return (
		<Box ref={wrapperRef}>
			<Popover autoFocus={false} isLazy isOpen={popover.isOpen} onClose={popover.onClose}>
				<PopoverAnchor>
					<Input
						border="none"
						isDisabled={isDisabled}
						onChange={debounce((e) => {
							handleChange(e.target.value);
						}, 200)}
						onKeyDown={(e) => {
							if (e.key === 'Escape') {
								popover.onClose();
							}
						}}
						placeholder={placeholder}
						px={3}
						_focus={{ boxShadow: 'none' }}
						_placeholder={{
							color: 'text.softest',
						}}
					></Input>
				</PopoverAnchor>

				<PopoverContent>
					<PopoverBody p={1}>
						<VStack as="ul" maxH={rem(360)} overflowY="auto">
							<Reveal if={!displayedOptions.length}>
								<Center p={2}>
									<Text color="text.softer">{noResultsMessage}</Text>
								</Center>
							</Reveal>
							{displayedOptions.map((option) => (
								<Box
									as="li"
									role="option"
									key={option.key}
									onClick={() => onSelect(option.key)}
									border={0}
									borderColor="border.soft"
									borderRadius="sm"
									borderStyle="solid"
									cursor="pointer"
									listStyleType="none"
									p={2}
									textAlign="left"
									transitionDuration="normal"
									transitionProperty="common"
									w="full"
									whiteSpace="nowrap"
									_focus={{ backgroundColor: 'secondary.bgAlpha' }}
									_hover={{ backgroundColor: 'secondary.bgAlphaHover' }}
								>
									<Box overflow="hidden" whiteSpace="nowrap" textOverflow="ellipsis">
										{option.name}
									</Box>
								</Box>
							))}
						</VStack>
					</PopoverBody>
				</PopoverContent>
			</Popover>
		</Box>
	);
};
